import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios'
import type {
  ApiClient,
  ApiRequestConfig,
  ApiResponse,
} from './api.interface.ts'

function toApiResponse<T>(response: AxiosResponse<T>): ApiResponse<T> {
  return {
    data: response.data,
    status: response.status,
    headers: response.headers as Record<string, string>,
  }
}

function toAxiosConfig(config?: ApiRequestConfig): AxiosRequestConfig {
  return {
    headers: config?.headers,
    params: config?.params,
    signal: config?.signal,
  }
}

export function createAxiosClient(): ApiClient {
  const instance: AxiosInstance = axios.create({
    /** Same-origin API (e.g. `/api/...` on the Vite dev server or Vercel). */
    baseURL: '',
    timeout: 30_000,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  })

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        localStorage.removeItem('auth_token')
        window.location.href = '/login'
      }
      return Promise.reject(error)
    },
  )

  return {
    async get<T>(url: string, config?: ApiRequestConfig) {
      const response = await instance.get<T>(url, toAxiosConfig(config))
      return toApiResponse(response)
    },
    async post<T>(url: string, data?: unknown, config?: ApiRequestConfig) {
      const response = await instance.post<T>(url, data, toAxiosConfig(config))
      return toApiResponse(response)
    },
    async put<T>(url: string, data?: unknown, config?: ApiRequestConfig) {
      const response = await instance.put<T>(url, data, toAxiosConfig(config))
      return toApiResponse(response)
    },
    async patch<T>(url: string, data?: unknown, config?: ApiRequestConfig) {
      const response = await instance.patch<T>(url, data, toAxiosConfig(config))
      return toApiResponse(response)
    },
    async delete<T>(url: string, config?: ApiRequestConfig) {
      const response = await instance.delete<T>(url, toAxiosConfig(config))
      return toApiResponse(response)
    },
  }
}
